/**
 * Shared status -> tone/label resolution and colour helpers for the CRM.
 *
 * Consolidates the duplicated status colour maps found across:
 *  - src/pages/crm/KycDetailsPage.tsx (`getStatusBadge`, ~L98)
 *  - src/pages/crm/KycManagementPage.tsx (`getStatusBadge`, ~L124)
 *  - src/pages/crm/Customers/components/KycTab.tsx (`getKycStatusBadge`, ~L46)
 *  - src/pages/crm/TransactionsPage.tsx (`getStatusBadge`, ~L406)
 *  - src/pages/crm/Customers/components/TransactionsTab.tsx (`getStatusClassName`, ~L138)
 *  - src/pages/crm/Customers/CustomersPage.tsx (`getStatusColor`, ~L634)
 *  - src/pages/crm/UsersPage.tsx (`getStatusColor`, ~L397)
 *  - src/pages/crm/components/HierarchyChart.tsx (`getStatusColor`)
 *  - src/pages/crm/LeadsPage.tsx (`getStatusColor`, ~L815)
 *  - src/pages/portal/trade/components/TradingActivity.tsx (`getStatusColor`)
 *  - src/components/trading/AutomationStatusWidget.tsx (`getStatusColor`)
 *  - src/components/shared/risk/RiskAlertSystem.tsx,
 *    src/components/shared/execution/ExecutionAlertConfiguration.tsx,
 *    src/components/dashboard/CustomerTradingActivityWidget.tsx,
 *    src/components/dashboard/ExecutionMonitoringWidget.tsx,
 *    src/components/trading/PositionAlertWidget.tsx,
 *    src/components/trading/StopLossTakeProfitWidget.tsx,
 *    src/components/trading/MarginCallWidget.tsx,
 *    src/pages/portal/analytics/RiskAnalyticsPage.tsx,
 *    src/pages/crm/trading/CustomerTradingDashboard.tsx,
 *    src/pages/crm/trading/RiskManagementTab.tsx (all `getSeverityColor`)
 *  - src/components/notes/NotesManager.tsx, src/components/notes/LeadNotesWidget.tsx,
 *    src/components/notes/NotesWidget.tsx (all `getPriorityColor`)
 *  - src/api/notifications/use-notifications.tsx (`getPriorityColor`, `getStatusColor`)
 *
 * A later task migrates callers to `resolveStatus`/`toneClasses`/`<StatusBadge>`
 * instead of a local map — the duplicates above are intentionally left in place.
 */

import type { CSSProperties } from 'react';

export type StatusTone = 'success' | 'warning' | 'destructive' | 'info' | 'neutral' | 'brand';

export type StatusKind =
    | 'kyc'
    | 'transaction'
    | 'trade'
    | 'account'
    | 'user'
    | 'lead'
    | 'severity'
    | 'priority'
    | 'notification'
    | 'campaign'
    | 'submission'
    | 'integration';

interface StatusEntry {
    tone: StatusTone;
    label: string;
}

/** Normalises `pending_review` / `pending-review` / `Pending Review` to the same key. */
function normalizeKey(status: string): string {
    return status
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
}

/** `pending_review` -> `Pending Review`. Used as the fallback label for unmapped statuses. */
function toTitleCase(key: string): string {
    return key
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

// Consolidated from KycDetailsPage.tsx, KycManagementPage.tsx and Customers/KycTab.tsx.
// NOTE: KycManagementPage previously labelled the pending state "Pending" while
// KycDetailsPage/KycTab used "Pending Review" — standardising on "Pending Review" here.
const KYC_STATUSES: Record<string, StatusEntry> = {
    approved: { tone: 'success', label: 'Approved' },
    rejected: { tone: 'destructive', label: 'Rejected' },
    pending: { tone: 'warning', label: 'Pending Review' },
    not_submitted: { tone: 'neutral', label: 'Not Submitted' },
};

// Consolidated from TransactionsPage.tsx (`getStatusBadge`) and
// Customers/components/TransactionsTab.tsx (`getStatusClassName`).
const TRANSACTION_STATUSES: Record<string, StatusEntry> = {
    pending: { tone: 'warning', label: 'Pending' },
    completed: { tone: 'success', label: 'Completed' },
    failed: { tone: 'destructive', label: 'Failed' },
    cancelled: { tone: 'neutral', label: 'Cancelled' },
};

// Consolidated from portal/trade/components/TradingActivity.tsx (`getStatusColor`)
// and components/trading/AutomationStatusWidget.tsx (`getStatusColor`).
const TRADE_STATUSES: Record<string, StatusEntry> = {
    filled: { tone: 'success', label: 'Filled' },
    completed: { tone: 'success', label: 'Completed' },
    pending: { tone: 'warning', label: 'Pending' },
    partial: { tone: 'warning', label: 'Partial' },
    open: { tone: 'warning', label: 'Open' },
    cancelled: { tone: 'destructive', label: 'Cancelled' },
    rejected: { tone: 'destructive', label: 'Rejected' },
    failed: { tone: 'destructive', label: 'Failed' },
    active: { tone: 'brand', label: 'Active' },
    running: { tone: 'brand', label: 'Running' },
};

// Consolidated from Customers/CustomersPage.tsx (`getStatusColor`, string-format branch).
const ACCOUNT_STATUSES: Record<string, StatusEntry> = {
    active: { tone: 'success', label: 'Active' },
    inactive: { tone: 'warning', label: 'Inactive' },
    suspended: { tone: 'destructive', label: 'Suspended' },
};

// Consolidated from UsersPage.tsx (`getStatusColor`) and components/HierarchyChart.tsx
// (`getStatusColor`), both keyed on `User['status']`.
const USER_STATUSES: Record<string, StatusEntry> = {
    active: { tone: 'success', label: 'Active' },
    inactive: { tone: 'warning', label: 'Inactive' },
    suspended: { tone: 'destructive', label: 'Suspended' },
};

// Consolidated from LeadsPage.tsx (`getStatusColor`, legacy string-format fallback branch).
// Lead statuses are normally tenant-defined with an API-supplied hex colour (see
// `statusColorStyle` below, which wins over this map whenever a colour is present) —
// this only covers the handful of legacy string statuses used as a fallback.
const LEAD_STATUSES: Record<string, StatusEntry> = {
    active: { tone: 'success', label: 'Active' },
    inactive: { tone: 'warning', label: 'Inactive' },
    suspended: { tone: 'destructive', label: 'Suspended' },
};

// Consolidated from RiskAlertSystem.tsx, ExecutionAlertConfiguration.tsx,
// CustomerTradingActivityWidget.tsx, ExecutionMonitoringWidget.tsx,
// PositionAlertWidget.tsx, StopLossTakeProfitWidget.tsx, MarginCallWidget.tsx,
// portal/analytics/RiskAnalyticsPage.tsx, crm/trading/CustomerTradingDashboard.tsx
// and crm/trading/RiskManagementTab.tsx (all `getSeverityColor`).
// The originals used 4 distinct hues (red/orange/yellow/blue) but the CRM's
// semantic token set only has 6 tones, so `high` and `medium` both collapse to
// `warning` here — they remain distinguishable by label.
const SEVERITY_STATUSES: Record<string, StatusEntry> = {
    critical: { tone: 'destructive', label: 'Critical' },
    high: { tone: 'warning', label: 'High' },
    medium: { tone: 'warning', label: 'Medium' },
    low: { tone: 'info', label: 'Low' },
};

// Consolidated from components/notes/NotesManager.tsx, components/notes/LeadNotesWidget.tsx,
// components/notes/NotesWidget.tsx (all `getPriorityColor`, Badge-variant based) and
// api/notifications/use-notifications.tsx (`getPriorityColor`, literal colour based).
// The notes components use `urgent` for the top priority while notifications use
// `critical` — both map to the same `destructive` tone here, keyed separately so the
// original label is preserved.
const PRIORITY_STATUSES: Record<string, StatusEntry> = {
    urgent: { tone: 'destructive', label: 'Urgent' },
    critical: { tone: 'destructive', label: 'Critical' },
    high: { tone: 'warning', label: 'High' },
    medium: { tone: 'warning', label: 'Medium' },
    low: { tone: 'success', label: 'Low' },
};

// Campaigns module (quantix-api/docs/campaigns/TASKS.md): draft/active/paused/archived.
const CAMPAIGN_STATUSES: Record<string, StatusEntry> = {
    draft: { tone: 'neutral', label: 'Draft' },
    active: { tone: 'success', label: 'Active' },
    paused: { tone: 'warning', label: 'Paused' },
    archived: { tone: 'neutral', label: 'Archived' },
};

// Campaigns module submissions (quantix-api/docs/campaigns/TASKS.md, field-mapping.md §5):
// pending/lead_created/linked_existing/failed/ignored.
const SUBMISSION_STATUSES: Record<string, StatusEntry> = {
    pending: { tone: 'warning', label: 'Pending' },
    lead_created: { tone: 'success', label: 'Lead Created' },
    linked_existing: { tone: 'info', label: 'Linked Existing' },
    failed: { tone: 'destructive', label: 'Failed' },
    ignored: { tone: 'neutral', label: 'Ignored' },
};

// Integrations page cards (connected / needs attention / not set up) and the
// payment providers' environment chips (live / sandbox).
const INTEGRATION_STATUSES: Record<string, StatusEntry> = {
    connected: { tone: 'success', label: 'Connected' },
    needs_attention: { tone: 'warning', label: 'Needs attention' },
    not_set_up: { tone: 'neutral', label: 'Not set up' },
    live: { tone: 'success', label: 'Live' },
    sandbox: { tone: 'info', label: 'Sandbox' },
};

// Consolidated from api/notifications/use-notifications.tsx (`getStatusColor`).
const NOTIFICATION_STATUSES: Record<string, StatusEntry> = {
    sent: { tone: 'success', label: 'Sent' },
    delivered: { tone: 'success', label: 'Delivered' },
    completed: { tone: 'success', label: 'Completed' },
    pending: { tone: 'warning', label: 'Pending' },
    scheduled: { tone: 'warning', label: 'Scheduled' },
    draft: { tone: 'warning', label: 'Draft' },
    failed: { tone: 'destructive', label: 'Failed' },
    cancelled: { tone: 'destructive', label: 'Cancelled' },
    read: { tone: 'info', label: 'Read' },
};

const STATUS_MAPS: Record<StatusKind, Record<string, StatusEntry>> = {
    kyc: KYC_STATUSES,
    transaction: TRANSACTION_STATUSES,
    trade: TRADE_STATUSES,
    account: ACCOUNT_STATUSES,
    user: USER_STATUSES,
    lead: LEAD_STATUSES,
    severity: SEVERITY_STATUSES,
    priority: PRIORITY_STATUSES,
    notification: NOTIFICATION_STATUSES,
    campaign: CAMPAIGN_STATUSES,
    submission: SUBMISSION_STATUSES,
    integration: INTEGRATION_STATUSES,
};

/**
 * Maps a raw API status string to a tone + display label, per domain.
 * Matching is case-insensitive and tolerates `pending_review`, `pending-review`
 * and `Pending Review` alike. Unmapped statuses fall back to `neutral` with a
 * title-cased version of the raw value; `null`/`undefined` falls back to
 * `neutral` / `'Unknown'`.
 */
export function resolveStatus(
    status: string | null | undefined,
    kind: StatusKind
): { tone: StatusTone; label: string } {
    if (!status || !status.trim()) {
        return { tone: 'neutral', label: 'Unknown' };
    }

    const key = normalizeKey(status);
    const entry = STATUS_MAPS[kind]?.[key];
    if (entry) return entry;

    return { tone: 'neutral', label: toTitleCase(key) || status };
}

const TONE_CLASSES: Record<StatusTone, string> = {
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/20',
    destructive: 'bg-destructive/10 text-destructive border-destructive/20',
    info: 'bg-info/10 text-info border-info/20',
    neutral: 'bg-muted text-muted-foreground border-border',
    brand: 'bg-primary/10 text-primary border-primary/20',
};

/** Tailwind classes for a tone, using semantic tokens only. */
export function toneClasses(tone: StatusTone): string {
    return TONE_CLASSES[tone];
}

interface Rgb {
    r: number;
    g: number;
    b: number;
}

function hexToRgb(hex: string): Rgb | null {
    const cleaned = hex.trim().replace(/^#/, '');
    const isShort = /^[0-9a-fA-F]{3}$/.test(cleaned);
    const isFull = /^[0-9a-fA-F]{6}$/.test(cleaned);
    if (!isShort && !isFull) return null;

    const full = isShort
        ? cleaned
              .split('')
              .map(c => c + c)
              .join('')
        : cleaned;

    const value = parseInt(full, 16);
    return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255,
    };
}

/** WCAG relative luminance (0 = black, 1 = white) for an sRGB colour. */
function relativeLuminance({ r, g, b }: Rgb): number {
    const channel = (v: number) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two relative luminances. */
function contrastRatio(l1: number, l2: number): number {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * For API-supplied hex colours (lead statuses): returns a style object with a
 * readable foreground, computed via WCAG relative luminance/contrast against
 * the supplied colour (picking whichever of black/white has the higher contrast
 * ratio) rather than always defaulting to white, which was unreadable on light
 * lead-status colours. Background and border use the supplied colour at a
 * reduced alpha, matching the `bg-<tone>/10 border-<tone>/20` convention used
 * for the built-in tones. Accepts 3- and 6-digit hex (with or without `#`);
 * returns `{}` for missing/invalid input so callers can fall back to `toneClasses`.
 */
export function statusColorStyle(hex?: string | null): CSSProperties {
    if (!hex) return {};

    const rgb = hexToRgb(hex);
    if (!rgb) return {};

    const luminance = relativeLuminance(rgb);
    const contrastWithBlack = contrastRatio(luminance, 0);
    const contrastWithWhite = contrastRatio(luminance, 1);
    const foreground = contrastWithBlack >= contrastWithWhite ? '#000000' : '#ffffff';

    const { r, g, b } = rgb;
    return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.18)`,
        borderColor: `rgba(${r}, ${g}, ${b}, 0.35)`,
        color: foreground,
    };
}
